import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { FiArrowUpRight } from 'react-icons/fi';
import { AiFillGithub } from 'react-icons/ai';

import { AppWrap, MotionWrap } from '../../wrapper';
import { urlFor, client } from '../../client';
import './Work.scss';

const Work = () => {
  const [works, setWorks] = useState([]);
  const [activeFilter, setActiveFilter] = useState('All');
  const [error, setError] = useState('');
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    const query = '*[_type == "works"] | order(_createdAt desc)';

    client.fetch(query)
      .then(setWorks)
      .catch(() => setError('Projects could not be loaded right now.'));
  }, []);

  const filters = useMemo(() => [
    'All',
    ...new Set(works.flatMap((work) => work.tags || []).filter((tag) => tag !== 'All')),
  ], [works]);

  const filteredWorks = activeFilter === 'All'
    ? works
    : works.filter((work) => work.tags?.includes(activeFilter));

  return (
    <>
      <div className="section-heading">
        <div>
          <p className="eyebrow">02 / Selected work</p>
          <h2 className="head-text">Ideas, shipped to <span>the browser.</span></h2>
        </div>
        <p className="section-heading__copy">
          A selection of frontend and full-stack builds. Filter by technology,
          explore the live product, or inspect the source.
        </p>
      </div>

      {error && <p className="status-message status-message--error" role="status">{error}</p>}

      {works.length > 0 && (
        <div className="app__work-toolbar">
          <div className="app__work-filter" aria-label="Filter projects">
            {filters.map((item) => (
              <button
                type="button"
                key={item}
                onClick={() => setActiveFilter(item)}
                className={activeFilter === item ? 'item-active' : ''}
                aria-pressed={activeFilter === item}
              >
                {item}
              </button>
            ))}
          </div>
          <p>{String(filteredWorks.length).padStart(2, '0')} projects</p>
        </div>
      )}

      <motion.div layout={!reduceMotion} className="app__work-portfolio">
        <AnimatePresence mode="popLayout">
          {filteredWorks.map((work, index) => (
            <motion.article
              layout={!reduceMotion}
              initial={reduceMotion ? false : { opacity: 0, y: 26 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduceMotion ? undefined : { opacity: 0, y: 12 }}
              transition={{ duration: 0.45, delay: reduceMotion ? 0 : index * 0.04 }}
              className="app__work-item"
              key={work._id || work.title}
            >
              <div className="app__work-img">
                <div className="browser-bar" aria-hidden="true">
                  <span /><span /><span />
                  <p>{work.title}</p>
                </div>
                {work.imgUrl && (
                  <img
                    src={urlFor(work.imgUrl).width(1200).height(860).fit('crop').auto('format').url()}
                    alt={`${work.title} project preview`}
                    loading="lazy"
                  />
                )}
                <div className="project-number">/{String(index + 1).padStart(2, '0')}</div>
              </div>

              <div className="app__work-content">
                <div>
                  <h3>{work.title}</h3>
                  {work.description && <p>{work.description}</p>}
                </div>
                <div className="app__work-actions" aria-label={`${work.title} links`}>
                  {work.projectLink && (
                    <a href={work.projectLink} target="_blank" rel="noreferrer">
                      Live site <FiArrowUpRight />
                    </a>
                  )}
                  {work.codeLink && (
                    <a href={work.codeLink} target="_blank" rel="noreferrer">
                      Source <AiFillGithub />
                    </a>
                  )}
                </div>
              </div>

              {work.tags?.length > 0 && (
                <ul className="app__work-tags" aria-label="Technologies">
                  {work.tags.filter((tag) => tag !== 'All').map((tag) => (
                    <li key={tag}>{tag}</li>
                  ))}
                </ul>
              )}
            </motion.article>
          ))}
        </AnimatePresence>
      </motion.div>
    </>
  );
};

export default AppWrap(
  MotionWrap(Work, 'app__works'),
  'work',
  'app__primarybg',
);
